import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import {
  MatFormField,
  MatLabel,
  MatSuffix,
} from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { SiaTranslatePipe, notify } from '@sia/shared';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'sia-change-password-dialog',
  templateUrl: './sia-change-password-dialog.html',
  styleUrl: './sia-change-password-dialog.scss',
  standalone: true,
  imports: [
    FormsModule,
    MatFormField,
    MatLabel,
    MatInputModule,
    MatIconButton,
    MatSuffix,
    MatIcon,
    MatButton,
    SiaTranslatePipe,
    MatDialogActions,
    MatDialogContent,
    NgTemplateOutlet,
    MatDialogTitle,
  ],
})
export class SiaChangePasswordDialogComponent {
  // injected dependencies
  #data = inject(MAT_DIALOG_DATA);
  #dialogRef =
    inject<MatDialogRef<SiaChangePasswordDialogComponent>>(MatDialogRef);
  #authService = inject(AuthService);

  // reactive state
  hideNewPassword = signal(true);
  hideNewPasswordConfirmation = signal(true);
  newPassword = signal<string | undefined>(undefined);
  newPasswordConfirmation = signal<string | undefined>(undefined);

  dialogData = signal<any>(this.#data);

  // computed password rules
  hasNumber = computed(() => !!this.newPassword()?.match(/\d/));
  hasCapitalCase = computed(() => !!this.newPassword()?.match(/[A-Z]/));
  hasSmallCase = computed(() => !!this.newPassword()?.match(/[a-z]/));
  hasSpecialCase = computed(() => !!this.newPassword()?.match(/[^A-Za-z 0-9]/));
  minLength = computed(() => (this.newPassword()?.length ?? 0) >= 8);
  same = computed(
    () =>
      this.newPassword() === this.newPasswordConfirmation() &&
      !!this.newPassword() &&
      !!this.newPasswordConfirmation(),
  );

  constructor() {
    this.#dialogRef.disableClose = true;
  }

  passwordChange(value: string) {
    this.newPassword.set(value);
  }

  passwordConfirmationChange(value: string) {
    this.newPasswordConfirmation.set(value);
  }

  async updatePassword() {
    try {
      await this.#authService.resetPassword(
        this.newPassword() ?? '',
        this.newPasswordConfirmation() ?? '',
      );
      notify('La password è stata cambiata con successo', 2000, 'success');
      this.closeDialog();
    } catch {
      notify(
        'La nuova password non è stata impostata a causa di un errore',
        3000,
        'error',
      );
    }
  }

  closeDialog() {
    this.#dialogRef.close(this.dialogData());
  }
  hideNewPasswordToggle(event: Event) {
    event.stopPropagation();
    this.hideNewPassword.set(!this.hideNewPassword());
  }
  hideNewPasswordConfirmationToggle(event: Event) {
    event.stopPropagation();
    this.hideNewPasswordConfirmation.set(!this.hideNewPasswordConfirmation());
  }
}
